import useAuth from "@irati/auth/helpers/auth/useAuth"
import {
  AddBasketItemPayload,
  CategoriesEmployeeOrganisation,
  CreatePayrollGivingOrderPayload,
  MutationAddBasketItemArgs,
  MutationCreatePayrollGivingQuoteArgs,
  MutationCreateStoreOrderArgs,
  MutationRemoveBasketItemArgs,
  MutationSetSalaryBracketArgs,
  MutationUpdateBasketItemArgs,
  PayrollOrder,
  PayrollPayBanner,
  PayrollPayBasket,
  PayrollPayCheckoutData,
  PayrollPayDepartment,
  PayrollPayFeaturedCategory,
  PayrollPayOrders,
  PayrollPayProductDetail,
  PayrollPayStoreOrder,
  PayrollPayStoreProducts,
  QueryCheckoutDataArgs,
  QueryDepartmentDataArgs,
  QueryLayoutDataArgs,
  QueryProductDetailArgs,
  QueryStoreBasketDataArgs,
  QueryStoreHomeBannersDataArgs,
  QueryStoreHomeDepartmentsDataArgs,
  QueryStoreHomeFeaturedCategoriesDataArgs,
  QueryStoreHomePopularBikesDataArgs,
  QueryStoreHomeSchemeDataArgs,
  QueryStoreProductsDataArgs,
  RemoveBasketItemPayload,
  SetSalaryBracketInput,
  StoreLayoutResponse,
  StoreScheme,
  UpdateBasketItemPayload
} from "@irati/types"
import {PRODUCTION} from "@irati/utils/constants/environment"
import env from "@irati/utils/env"
import axios from "axios"

import {createUseLazyQuery, createUseMutation, createUseQuery} from "@irati/utils/services/apollo"
import {addPayrollOrderCommentKey, addPayrollOrderCommentMutation} from "../apollo/mutation/AddPayrollOrderComment"
import {organisationalApprovalKey, organisationalApprovalMutation} from "../apollo/mutation/OrganisationalApproval"
import {updatePayrollOrderKey, updatePayrollOrderMutation} from "../apollo/mutation/UpdatePayrollOrder"
import addBasketItemMutation, {getAddBasketItemMutationKey} from "../apollo/query/AddBasketItem"
import getStoreDepartmentsQuery, {getStoreDepartmentsQueryKey} from "../apollo/query/AllDepartments"
import createStoreOrderMutation, {getCreateStoreOrderMutationKey} from "../apollo/query/CreateOrder"
import {createTravelAndLeisureOrderMutation, getCreateTravelAndLeisureOrderMutationKey} from "../apollo/query/CreateTravelLeisureOrder"
import getDepartmentCategoriesQuery, {getDepartmentCategoriesQueryKey} from "../apollo/query/DepartmentCategories"
import {exportPayrollOrdersQuery, getExportPayrollOrdersQueryKey} from "../apollo/query/ExportPayrollOrders"
import getFeaturedCategoriesQuery, {getFeaturedCategoriesQueryKey} from "../apollo/query/FeaturedCategories"
import getLandingBannersQuery, {getLandingBannersQueryKey} from "../apollo/query/LandingBanners"
import {getLayoutDataQueryKey, layoutDataQuery} from "../apollo/query/LayoutData"
import {getListPayrollOrdersQueryKey, listPayrollOrdersQuery} from "../apollo/query/ListPayrollOrders"
import {getOrderAuditLogsQueryKey, orderAuditLogsQuery} from "../apollo/query/OrderAuditLogs"
import {getOrderDocumentsQueryKey, orderDocumentsQuery} from "../apollo/query/OrderDocuments"
import {getOrderFiltersQueryKey, orderFiltersQuery} from "../apollo/query/OrderFilters"
import {getPopularBikesQueryKey, popularBikesQuery} from "../apollo/query/PopularBikes"
import getProductDetailQuery, {getProductDetailQueryKey} from "../apollo/query/ProductDetail"
import removeBasketItemMutation, {getRemoveBasketItemMutationKey} from "../apollo/query/RemoveBasketItem"
import {getSalaryBracketsQueryKey, salaryBracketsQuery} from "../apollo/query/SalaryBrackets"
import {setSalaryBracketMutation, setSalaryBracketMutationKey} from "../apollo/query/SetSalaryBracket"
import getBasketDataQuery, {getBasketDataQueryKey} from "../apollo/query/StoreBasketData"
import getCheckoutDataQuery, {getCheckoutDataQueryKey} from "../apollo/query/StoreCheckoutData"
import {getStoreHomeSchemeQueryKey, storeHomeSchemeQuery} from "../apollo/query/StoreHomeScheme"
import getStoreProductsQuery, {getStoreProductsQueryKey} from "../apollo/query/StoreProducts"
import updateBasketItemMutation, {getUpdateBasketItemMutationKey} from "../apollo/query/UpdateBasketItem"
import {
  getUpdateBasketItemsBrochureGroupMutationKey,
  updateBasketItemsBrochureGroupMutation
} from "../apollo/query/UpdateBasketItemsBrochureGroup"
import {
  createPayrollGivingOrderMutation,
  getCreatePayrollGivingOrderMutationKey
} from "../apollo/mutation/CreatePayrollGivingOrderMutation"
import {getViewPayrollOrderQueryKey, viewPayrollOrderQuery} from "../apollo/query/ViewPayrollOrder"

export const useViewOrderQuery = createUseQuery<PayrollOrder>(viewPayrollOrderQuery, {
  key: getViewPayrollOrderQueryKey
})

export const useListOrdersQuery = createUseLazyQuery<PayrollPayOrders>(listPayrollOrdersQuery, {
  key: getListPayrollOrdersQueryKey
})

export const useOrderFiltersQuery = createUseLazyQuery<PayrollPayOrders>(orderFiltersQuery, {
  key: getOrderFiltersQueryKey
})

export const useOrganisationalApprovalMutation = createUseMutation(organisationalApprovalMutation, {
  key: organisationalApprovalKey
})

export const useAddPayrollOrderCommentMutation = createUseMutation(addPayrollOrderCommentMutation, {
  key: addPayrollOrderCommentKey
})

export const useUpdatePayrollOrder = createUseMutation(updatePayrollOrderMutation, {
  key: updatePayrollOrderKey
})

export const useOrderDocuments = createUseQuery(orderDocumentsQuery, {
  key: getOrderDocumentsQueryKey
})

export const useOrderAuditLog = createUseQuery(orderAuditLogsQuery, {
  key: getOrderAuditLogsQueryKey
})

export const useExportOrdersQuery = createUseLazyQuery(exportPayrollOrdersQuery, {
  key: getExportPayrollOrdersQueryKey
})

export const importOrdersMutation = (file: File) => {
  const auth = useAuth()
  const formData = new FormData()
  formData.append("file", file)
  formData.append("actor_identifier", auth.user?.uuid)
  formData.append("client_id", auth.business?.uuid)
  const baseUrl =
    env.ENVIRONMENT_NAME === PRODUCTION ? "https://api.vivup.co.uk/v2/partner" : "https://pull-1623.qa.vivup.co.uk/api/v2/partner"

  return axios.post(`${baseUrl}/orders/import`, formData, {
    headers: {
      Authorization: `Bearer ${process.env.ORDER_MANAGEMENT_TOKEN}`,
      "Content-Type": "multipart/form-data"
    }
  })
}

export const useStoreSchemeQuery = createUseQuery<StoreScheme, QueryStoreHomeSchemeDataArgs>(storeHomeSchemeQuery, {
  key: getStoreHomeSchemeQueryKey
})

export const useStoreBannersQuery = createUseQuery<PayrollPayBanner, QueryStoreHomeBannersDataArgs>(getLandingBannersQuery, {
  key: getLandingBannersQueryKey
})

export const useFeaturedCategoriesQuery = createUseQuery<PayrollPayFeaturedCategory, QueryStoreHomeFeaturedCategoriesDataArgs>(
  getFeaturedCategoriesQuery,
  {
    key: getFeaturedCategoriesQueryKey
  }
)

export const useStoreDepartmentsQuery = createUseQuery<PayrollPayDepartment, QueryStoreHomeDepartmentsDataArgs>(getStoreDepartmentsQuery, {
  key: getStoreDepartmentsQueryKey
})

export const usePopularBikesQuery = createUseQuery<PayrollPayStoreProducts, QueryStoreHomePopularBikesDataArgs>(popularBikesQuery, {
  key: getPopularBikesQueryKey
})

export const useLayoutDataQuery = createUseQuery<StoreLayoutResponse, QueryLayoutDataArgs>(layoutDataQuery, {
  key: getLayoutDataQueryKey,
  errorHandlers: [
    (code) => {
      switch (code) {
        case "PBPRPNoSalaryBracket":
          return "PBPRPNoSalaryBracket"
      }
    }
  ]
})

export const useDepartmentCategoriesQuery = createUseQuery<CategoriesEmployeeOrganisation, QueryDepartmentDataArgs>(
  getDepartmentCategoriesQuery,
  {
    key: getDepartmentCategoriesQueryKey,
    errorHandlers: [
      (code) => {
        switch (code) {
          case "PBPRPNoSalaryBracket":
            return "PBPRPNoSalaryBracket"
        }
      }
    ]
  }
)

export const useStoreProductsQuery = createUseQuery<PayrollPayStoreProducts, QueryStoreProductsDataArgs>(getStoreProductsQuery, {
  key: getStoreProductsQueryKey
})

export const useProductDetailQuery = createUseQuery<PayrollPayProductDetail, QueryProductDetailArgs>(getProductDetailQuery, {
  key: getProductDetailQueryKey
})

export const useBasketDataQuery = createUseQuery<PayrollPayBasket, QueryStoreBasketDataArgs>(getBasketDataQuery, {
  key: getBasketDataQueryKey
})

export const useAddBasketItemMutation = createUseMutation<AddBasketItemPayload, MutationAddBasketItemArgs>(addBasketItemMutation, {
  key: getAddBasketItemMutationKey
})

export const useUpdateBasketItemMutation = createUseMutation<UpdateBasketItemPayload, MutationUpdateBasketItemArgs>(
  updateBasketItemMutation,
  {
    key: getUpdateBasketItemMutationKey
  }
)

export const useRemoveBasketItemMutation = createUseMutation<RemoveBasketItemPayload, MutationRemoveBasketItemArgs>(
  removeBasketItemMutation,
  {
    key: getRemoveBasketItemMutationKey
  }
)

export const useCheckoutDataQuery = createUseQuery<PayrollPayCheckoutData, QueryCheckoutDataArgs>(getCheckoutDataQuery, {
  key: getCheckoutDataQueryKey
})

export const useCreateStoreOrderMutation = createUseMutation<PayrollPayStoreOrder, MutationCreateStoreOrderArgs>(
  createStoreOrderMutation,
  {
    key: getCreateStoreOrderMutationKey
  }
)

export const useCalculatorData = createUseQuery<PayrollPayProductDetail, QueryProductDetailArgs>(getProductDetailQuery, {
  key: getProductDetailQueryKey
})

export const useSalaryBracketsQuery = createUseQuery(salaryBracketsQuery, {
  key: getSalaryBracketsQueryKey
})

export const useSetSalaryBracketMutation = createUseMutation<SetSalaryBracketInput, MutationSetSalaryBracketArgs>(
  setSalaryBracketMutation,
  {
    key: setSalaryBracketMutationKey
  }
)

export const useCreateTravelAndLeisureOrderMutation = createUseMutation(createTravelAndLeisureOrderMutation, {
  key: getCreateTravelAndLeisureOrderMutationKey
})

export const useUpdateBasketItemsBrochureGroupMutation = createUseMutation(updateBasketItemsBrochureGroupMutation, {
  key: getUpdateBasketItemsBrochureGroupMutationKey
})

export const useCreatePayrollGivingOrderMutation = createUseMutation<
  CreatePayrollGivingOrderPayload,
  MutationCreatePayrollGivingQuoteArgs
>(createPayrollGivingOrderMutation, {
  key: getCreatePayrollGivingOrderMutationKey
})
